/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { cloneElement, PureComponent, Children } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { range, isEqual, some } from 'underscore';
import UIFlex from 'UIComponents/layout/UIFlex';
import { HORIZONTAL, VERTICAL } from 'DesignData/enums/AxisDirections';
import { PERCENT, PIXELS } from 'DesignData/enums/SizeUnits';
import { isNullOrUndefined } from 'DesignData/lib/validation';
import Resizer from 'DesignComponents/resizablePanes/Resizer';
import PaneSize from 'DesignData/models/PaneSize';
export default class ResizablePane extends PureComponent {
  constructor(...args) {
    super(...args);
    this.state = {
      sizes: {}
    };

    this.onMouseDown = (index, e) => {
      this.setPrevCoordinate(e);

      this.onMouseMoveWithIndex = (...partialArgs) => this.onMouseMove(index, ...partialArgs);

      document.addEventListener('mousemove', this.onMouseMoveWithIndex);
      document.addEventListener('mouseup', this.onMouseUp);
    };

    this.onMouseMove = (index, e) => {
      e.preventDefault();
      const difference = this.isVertical() ? e.clientX - this.prevX : e.clientY - this.prevY;
      this.setPrevCoordinate(e);
      this.initialSizes = Children.map(this.props.children, (_child, _index) => {
        const rect = findDOMNode(this[`ref-${_index}`]).getBoundingClientRect();
        return this.isVertical() ? rect.width : rect.height;
      });
      const [firstIndex, secondIndex] = this.getIndicesToResize(index, difference);

      if (isNullOrUndefined(firstIndex) || isNullOrUndefined(secondIndex)) {
        return;
      }

      this.onResize(firstIndex, secondIndex, difference);
    };

    this.onMouseUp = () => {
      const {
        children: _children,
        onPaneResize
      } = this.props;
      const children = Children.toArray(_children);
      const {
        sizes
      } = this.state;
      document.removeEventListener('mousemove', this.onMouseMoveWithIndex);
      document.removeEventListener('mouseup', this.onMouseUp);

      if (onPaneResize) {
        const sizesById = Object.keys(sizes).reduce((acc, index) => {
          const paneId = children[index].props.paneId;

          if (!isNullOrUndefined(paneId)) {
            return Object.assign({}, acc, {
              [paneId]: sizes[index]
            });
          }

          return acc;
        }, {});
        onPaneResize(sizesById);
      }
    };

    this.setChildRef = (index, ref) => {
      const componentRef = ref && ref.getWrappedInstance ? ref.getWrappedInstance() : ref;
      this[`ref-${index}`] = componentRef;
    };
  }

  componentDidUpdate(prevProps) {
    const children = Children.toArray(this.props.children);
    const defaultValuesChanged = some(children, (child, index) => {
      const nextDefaultSize = child.props.defaultSize;
      const prevDefaultSize = this.getChildProp(index, 'defaultSize', prevProps);
      return !isEqual(nextDefaultSize, prevDefaultSize);
    });

    if (defaultValuesChanged) {
      this.setState({
        sizes: {}
      });
    }
  }

  isVertical() {
    return this.props.direction === VERTICAL;
  }

  getContainerSize() {
    const {
      width,
      height
    } = findDOMNode(this).getBoundingClientRect();
    return this.isVertical() ? width : height;
  }

  getChildProp(index, propName, props = null) {
    const {
      children: _children
    } = props || this.props;
    const children = Children.toArray(_children);
    return children[index] ? children[index].props[propName] : null;
  }

  getChildMinSize(index) {
    const minSize = this.getChildProp(index, 'minSize');

    if (!minSize) {
      return null;
    }

    const {
      value,
      units
    } = minSize;
    return units === PERCENT ? value / 100 * this.getContainerSize() : value;
  }

  getNewSize(index, newSize) {
    const {
      paneMinSize
    } = this.props;
    const childMinSize = this.getChildMinSize(index);

    if (childMinSize) {
      return Math.max(childMinSize, newSize);
    }

    return Math.max(paneMinSize, newSize);
  }

  getNewSizeObject(index, pixelValue) {
    const unitsOnResize = this.getChildProp(index, 'unitsOnResize');
    const isMinSize = pixelValue === this.getChildMinSize(index) || pixelValue === this.props.paneMinSize;

    if (unitsOnResize === PIXELS) {
      return new PaneSize({
        value: pixelValue,
        units: PIXELS,
        isMinSize
      });
    }

    return new PaneSize({
      value: pixelValue / this.getContainerSize() * 100,
      units: PERCENT,
      isMinSize
    });
  }

  findNextResizableIndex(start, stop, difference) {
    const {
      paneMinSize
    } = this.props;
    const step = start > stop ? -1 : 1; // don't resize pane if:
    // - it has resizeDisabled set to true
    // - it's the min size and the next mouse move would shrink it further

    const nextResizableIndex = range(start, stop, step).find(index => {
      if (this.getChildProp(index, 'resizeDisabled')) {
        return false;
      }

      const size = this.initialSizes[index];
      return size > paneMinSize || size <= paneMinSize && difference > 0;
    });
    return !isNullOrUndefined(nextResizableIndex) ? nextResizableIndex : -1;
  }

  getIndicesToResize(index, difference) {
    const {
      children
    } = this.props;
    const firstIndex = this.findNextResizableIndex(index, -1, difference); // -1 b/c second param is exclusive

    const secondIndex = this.findNextResizableIndex(index + 1, Children.count(children), difference * -1);
    return !~firstIndex || !~secondIndex ? [] : [firstIndex, secondIndex];
  }

  onResize(firstIndex, secondIndex, difference) {
    const {
      paneMinSize
    } = this.props;
    const firstMinSize = this.getChildMinSize(firstIndex);
    const secondMinSize = this.getChildMinSize(secondIndex);
    const firstSize = this.initialSizes[firstIndex];
    const secondSize = this.initialSizes[secondIndex];
    let newFirstSize = firstSize + difference;
    let newSecondSize = secondSize - difference;

    if (newFirstSize < firstMinSize || newFirstSize < paneMinSize) {
      newFirstSize = this.getNewSize(firstIndex, newFirstSize);
      newSecondSize = firstSize + secondSize - newFirstSize;
    } else if (newSecondSize < secondMinSize || newSecondSize < paneMinSize) {
      newSecondSize = this.getNewSize(secondIndex, newSecondSize);
      newFirstSize = firstSize + secondSize - newSecondSize;
    }

    const sizes = Object.assign({}, this.state.sizes, {
      [firstIndex]: this.getNewSizeObject(firstIndex, newFirstSize),
      [secondIndex]: this.getNewSizeObject(secondIndex, newSecondSize)
    }); // Fill in missing sizes. This occurs on first use.

    this.initialSizes.forEach((size, idx) => {
      if (sizes[idx]) return;
      sizes[idx] = this.getNewSizeObject(idx, size);
    });
    this.setState({
      sizes
    });
  }

  setPrevCoordinate(e) {
    if (this.isVertical()) {
      this.prevX = e.clientX;
    } else {
      this.prevY = e.clientY;
    }
  }

  renderPanesAndResizers() {
    const {
      children,
      direction,
      resizerClassName
    } = this.props;
    const {
      sizes
    } = this.state;
    const panesAndResizers = [];
    Children.forEach(children, (child, index) => {
      const childNode = /*#__PURE__*/cloneElement(child, Object.assign({
        direction
      }, resizerClassName && {
        resizerClassName
      }, {
        size: sizes[index],
        key: `child-${index}`,
        ref: (...partialArgs) => this.setChildRef(index, ...partialArgs)
      }));
      panesAndResizers.push(childNode);
      const resizeDisabled = this.getChildProp(index, 'resizeDisabled');

      if (index < Children.count(children) - 1 && !resizeDisabled) {
        panesAndResizers.push( /*#__PURE__*/_jsx(Resizer, {
          direction: direction,
          onMouseDown: (...partialArgs) => this.onMouseDown(index, ...partialArgs),
          className: resizerClassName
        }, `resizer-${index}`));
      }
    });
    return panesAndResizers;
  }

  render() {
    const {
      className
    } = this.props;
    const flexDirection = this.isVertical() ? 'row' : 'column';
    const panesClassName = classNames('resizable-panes', className);
    return /*#__PURE__*/_jsx(UIFlex, {
      align: "stretch",
      className: panesClassName,
      direction: flexDirection,
      children: this.renderPanesAndResizers()
    });
  }

}
ResizablePane.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  direction: PropTypes.oneOf([HORIZONTAL, VERTICAL]),
  onPaneResize: PropTypes.func,
  paneMinSize: PropTypes.number,
  resizerClassName: PropTypes.string
};
ResizablePane.defaultProps = {
  direction: HORIZONTAL,
  paneMinSize: 10
};